import { can } from '@misumi/shared'
import type { CustomerRole } from '@misumi/shared'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { InvitationsTable } from '../components/admin/InvitationsTable'
import { InviteUserForm } from '../components/admin/InviteUserForm'
import { JoinRequestsTable } from '../components/admin/JoinRequestsTable'
import { FormAlert } from '../components/auth/FormAlert'
import { apiClient } from '../lib/api/client'
import type { components } from '../lib/api/schema.gen'
import { useAuth } from '../lib/auth/auth-context'
import {
  type ProblemCopy,
  invitationProblemCopy,
  joinRequestDecisionProblemCopy,
} from '../lib/auth/problem-messages'

type Invitation = components['schemas']['Invitation']
type JoinRequestView = components['schemas']['JoinRequestView']

/**
 * "Manage users" admin console (F0.6, USP-36, PR-ONB-01) — the visible half
 * of the invite + role API (S0.9/S0.10). Reached by a fully-authenticated
 * `account_admin`, NOT the pre-onboarding flows F0.5/F0.7 gate on — see this
 * file's own gate below for why it does not reuse their
 * `status !== 'onboarding_required'` guard pattern.
 *
 * SCOPE, per the contract read against `apps/api/openapi.yaml` /
 * `apps/api/src/modules/onboarding/routes.ts` before building this (flag,
 * don't invent, matching the F0.3-F0.7 discipline):
 *
 * - There is NO endpoint to list an account's EXISTING members, and NO
 *   endpoint to change an existing member's role or deactivate one. The
 *   `Membership` schema exists but nothing serves it. The Gate 2 UX spec
 *   itself flags this (§11 OQ-33): the invite/role slice (this screen) was
 *   designed, but a fuller account/contact-management surface was not. This
 *   screen therefore covers exactly what IS backed: invite a new teammate
 *   with a role (`POST /onboarding/invitations`), see/revoke pending and
 *   past invitations (`GET`/`POST .../revoke`), and see/decide pending
 *   requests to join from people who matched the account's verified email
 *   domain (`GET /onboarding/join-requests`, `POST .../approve|deny`) — S0.10.
 *   A "see and manage existing users" table (name/status/deactivate/change
 *   role) needs a new backend surface; flagged forward rather than built
 *   against nothing.
 * - CONTRACT GAP: `openapi.yaml`'s `/onboarding/join-requests/{id}/approve`
 *   and `.../deny` paths use a `{id}` template but never declare a `path`
 *   `parameters` block for it (unlike `revokeInvitation`, which does) — so
 *   the generated operation type has `path?: never` and the typed client
 *   cannot express the id substitution without a cast. `approveJoinRequest`/
 *   `denyJoinRequest` below cast the call `as never` (same escape hatch
 *   `auth-context.tsx` already uses for the intentionally-uncontracted
 *   `/auth/logout`) — the runtime request is correct (openapi-fetch
 *   interpolates `{id}` from `params.path` regardless of the declared type),
 *   but the spec itself should gain the missing `parameters` block; flagged
 *   forward, not silently worked around.
 *
 * Gate: `auth.status === 'authenticated'` AND `can(role, 'user:manage')`
 * (`@misumi/shared` — the same capability/role vocabulary
 * `apps/api/src/modules/onboarding/invitation.ts` enforces server-side via
 * `requireCapability('user:manage')`). The server is authoritative; this
 * gate is UX only — hiding the console from a non-admin does not, by itself,
 * stop them from calling the API directly, and the server independently
 * refuses with 403 either way. `can()` is used (least-privilege,
 * auditability, C26) rather than a bare `role === 'account_admin'` string
 * check, so a future capability-set change here doesn't require touching
 * every gate that reads it.
 *
 * Why this does NOT reuse `<CreateCompanyPage>`/`<RequestToJoinPage>`'s
 * inline `if (auth.status !== 'onboarding_required') return <dead-end/>`
 * shape as a shared hook: those two guard a *session-status* value
 * (`onboarding_required`) that exists for exactly one pre-membership moment;
 * this screen guards a *capability* derived from an authenticated session's
 * role, a completely different predicate with a different dead-end (a
 * signed-in non-admin sees "you don't have access", not "sign in"). A
 * shared `usePreOnboardingGuard`/`<SessionExpiredAlert>` extraction (a
 * standing code-review suggestion) was assessed here and does NOT apply —
 * this gate has no session-expired-mid-flow special case to share (a 401 on
 * any mutation below is a genuinely-authenticated session expiring, which
 * `authMiddleware`'s global `invalidateSession()` already handles by
 * flipping `auth.status` to `unauthenticated` and letting this component's
 * own re-render show the sign-in dead-end — no bespoke 401 branch needed,
 * unlike F0.5/F0.7's pre-onboarding-only `AUTH_SURFACE_PATHS` carve-out).
 * The two pre-onboarding pages are left as-is (still 2 occurrences of that
 * inline shape) rather than force a shared abstraction that doesn't fit.
 */
export function ManageUsersPage() {
  const auth = useAuth()

  const [invitations, setInvitations] = useState<Invitation[]>([])
  const [joinRequests, setJoinRequests] = useState<JoinRequestView[]>([])
  const [loadError, setLoadError] = useState<ProblemCopy | undefined>()

  const [email, setEmail] = useState('')
  const [role, setRole] = useState<CustomerRole>('buyer')
  const [emailError, setEmailError] = useState<string | undefined>()
  const [inviteSubmitting, setInviteSubmitting] = useState(false)
  const [inviteServerError, setInviteServerError] = useState<ProblemCopy | undefined>()

  const [revokingId, setRevokingId] = useState<string | undefined>()
  const [decidingId, setDecidingId] = useState<string | undefined>()
  const [statusMessage, setStatusMessage] = useState<string | undefined>()

  const alertRef = useRef<HTMLDivElement | null>(null)
  const emailInputRef = useRef<HTMLInputElement | null>(null)

  const isAuthorized =
    auth.status === 'authenticated' && can(auth.session.accountContext.role, 'user:manage')

  const loadInvitationsAndRequests = useCallback(async () => {
    setLoadError(undefined)
    try {
      const [invitationsResult, joinRequestsResult] = await Promise.all([
        apiClient.GET('/onboarding/invitations'),
        apiClient.GET('/onboarding/join-requests'),
      ])
      if (invitationsResult.data) {
        setInvitations(invitationsResult.data.invitations)
      }
      if (joinRequestsResult.data) {
        setJoinRequests(joinRequestsResult.data.joinRequests)
      }
      // A 401 from either call is handled globally (see doc comment above) —
      // `auth.status` flips and this component re-renders the sign-in
      // dead-end. Any other error surfaces as a page-level load error.
      if (!invitationsResult.data && invitationsResult.error?.status !== 401) {
        setLoadError({
          title: "Couldn't load invitations",
          detail: invitationsResult.error?.detail ?? 'Please try again.',
        })
      } else if (!joinRequestsResult.data && joinRequestsResult.error?.status !== 401) {
        setLoadError({
          title: "Couldn't load join requests",
          detail: joinRequestsResult.error?.detail ?? 'Please try again.',
        })
      }
    } catch {
      setLoadError({ title: 'Something went wrong', detail: 'Please try again.' })
    }
  }, [])

  useEffect(() => {
    if (isAuthorized) {
      void loadInvitationsAndRequests()
    }
  }, [isAuthorized, loadInvitationsAndRequests])

  useEffect(() => {
    if (inviteServerError || loadError) {
      alertRef.current?.focus()
    }
  }, [inviteServerError, loadError])

  // USP-42 pattern: a client-validation error moves focus to the invalid field.
  useEffect(() => {
    if (emailError) {
      emailInputRef.current?.focus()
    }
  }, [emailError])

  async function handleInvite() {
    const trimmedEmail = email.trim()
    if (!trimmedEmail) {
      setEmailError('Enter an email address.')
      return
    }
    setEmailError(undefined)
    setInviteServerError(undefined)
    setInviteSubmitting(true)
    try {
      const { data, error } = await apiClient.POST('/onboarding/invitations', {
        body: { email: trimmedEmail, role },
      })
      if (data) {
        setInvitations((current) => [data, ...current])
        setEmail('')
        setRole('buyer')
        setStatusMessage(`Invite sent to ${data.email}.`)
        return
      }
      if (error?.status === 401) {
        return // Handled globally — see doc comment above.
      }
      setInviteServerError(invitationProblemCopy(error?.type, error?.detail))
    } catch {
      setInviteServerError({ title: 'Something went wrong', detail: 'Please try again.' })
    } finally {
      setInviteSubmitting(false)
    }
  }

  async function handleRevoke(id: string) {
    setInviteServerError(undefined)
    setRevokingId(id)
    try {
      const { data, error } = await apiClient.POST('/onboarding/invitations/{id}/revoke', {
        params: { path: { id } },
      })
      if (data) {
        setInvitations((current) =>
          current.map((invitation) => (invitation.id === id ? data : invitation)),
        )
        setStatusMessage(`Invite to ${data.email} revoked.`)
        return
      }
      if (error?.status === 401) {
        return // Handled globally.
      }
      setInviteServerError(invitationProblemCopy(error?.type, error?.detail))
    } catch {
      setInviteServerError({ title: 'Something went wrong', detail: 'Please try again.' })
    } finally {
      setRevokingId(undefined)
    }
  }

  async function handleApprove(id: string, grantedRole: CustomerRole) {
    setInviteServerError(undefined)
    setDecidingId(id)
    try {
      // See this component's CONTRACT GAP doc comment: the operation's
      // generated type has `path?: never` because openapi.yaml never
      // declares the `{id}` path parameter for this operation, so the call
      // is cast — the runtime request still correctly substitutes `id`.
      const { data, error } = await apiClient.POST(
        '/onboarding/join-requests/{id}/approve' as never,
        {
          params: { path: { id } },
          body: { role: grantedRole },
        } as never,
      )
      if (data) {
        setJoinRequests((current) =>
          current.map((joinRequest) =>
            joinRequest.id === id ? { ...joinRequest, status: 'approved' } : joinRequest,
          ),
        )
        setStatusMessage(`Request approved — granted ${grantedRole.replace('_', ' ')}.`)
        return
      }
      if ((error as { status?: number } | undefined)?.status === 401) {
        return // Handled globally.
      }
      const typedError = error as { type?: string; detail?: string } | undefined
      setInviteServerError(joinRequestDecisionProblemCopy(typedError?.type, typedError?.detail))
    } catch {
      setInviteServerError({ title: 'Something went wrong', detail: 'Please try again.' })
    } finally {
      setDecidingId(undefined)
    }
  }

  async function handleDeny(id: string) {
    setInviteServerError(undefined)
    setDecidingId(id)
    try {
      const { data, error } = await apiClient.POST(
        '/onboarding/join-requests/{id}/deny' as never,
        { params: { path: { id } } } as never,
      )
      if (data) {
        setJoinRequests((current) =>
          current.map((joinRequest) =>
            joinRequest.id === id ? { ...joinRequest, status: 'denied' } : joinRequest,
          ),
        )
        setStatusMessage('Request denied.')
        return
      }
      if ((error as { status?: number } | undefined)?.status === 401) {
        return // Handled globally.
      }
      const typedError = error as { type?: string; detail?: string } | undefined
      setInviteServerError(joinRequestDecisionProblemCopy(typedError?.type, typedError?.detail))
    } catch {
      setInviteServerError({ title: 'Something went wrong', detail: 'Please try again.' })
    } finally {
      setDecidingId(undefined)
    }
  }

  // See <BillingPage>: `loading` is not `unauthenticated`, so the dead-end must
  // wait for the probe rather than flash at a signed-in admin.
  if (auth.status === 'loading') {
    return null
  }

  if (auth.status !== 'authenticated') {
    return (
      <section className="auth-page">
        <h1 className="display-heading">Sign in to manage users</h1>
        <p>We need to confirm it&rsquo;s you before showing your account&rsquo;s users.</p>
        <p>
          <Link to="/login">Sign in</Link>
        </p>
      </section>
    )
  }

  if (!isAuthorized) {
    return (
      <section className="auth-page">
        <h1 className="display-heading">You don&rsquo;t have access to manage users</h1>
        <p>This page is available to account admins only.</p>
        <p>
          <Link to="/">Back home</Link>
        </p>
      </section>
    )
  }

  const alertToShow = inviteServerError ?? loadError

  return (
    <section>
      <h1 className="display-heading">Manage users</h1>
      <p aria-live="polite" className="sr-only">
        {statusMessage}
      </p>
      {alertToShow && (
        <FormAlert ref={alertRef} title={alertToShow.title} detail={alertToShow.detail} />
      )}

      <h2 className="section-heading" id="invite-user-heading">
        Invite a teammate
      </h2>
      <InviteUserForm
        ref={emailInputRef}
        email={email}
        onEmailChange={setEmail}
        emailError={emailError}
        role={role}
        onRoleChange={setRole}
        submitting={inviteSubmitting}
        onSubmit={() => void handleInvite()}
      />

      <h2 className="section-heading">Invitations</h2>
      <InvitationsTable
        invitations={invitations}
        revokingId={revokingId}
        onRevoke={(id) => void handleRevoke(id)}
      />

      <h2 className="section-heading">Requests to join</h2>
      <JoinRequestsTable
        joinRequests={joinRequests}
        decidingId={decidingId}
        onApprove={(id, grantedRole) => void handleApprove(id, grantedRole)}
        onDeny={(id) => void handleDeny(id)}
      />
    </section>
  )
}
